import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { VisuallyHidden } from './VisuallyHidden.js';
import './Checkbox.css';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Visible label — required. An unlabelled checkbox with only a visual
   * checkmark is exactly the colour/shape-only signalling a11y.md §2 bans. */
  label: ReactNode;
  /**
   * Hide the label visually while keeping it as the accessible name.
   *
   * The label stays **required** — this is not an escape hatch from that rule, it
   * is the case where the visible context is elsewhere: a row-selection checkbox
   * in a `DataTable` sits beside the row's own name, so repeating it would be
   * visual noise, while a screen-reader user hears "checkbox" with no idea which
   * row it belongs to. Use this only when the surrounding row supplies the visual
   * context; never to avoid writing a label.
   */
  hideLabel?: boolean;
}

/** A real native `<input type="checkbox">`, visually restyled — never a
 * div-with-onClick, which is what the prototype's toggles amounted to. */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, className, id, hideLabel, ...rest },
  ref
) {
  return (
    <label className={['dpr-checkbox', className].filter(Boolean).join(' ')}>
      <input ref={ref} type="checkbox" id={id} className="dpr-checkbox__input" {...rest} />
      <span className="dpr-checkbox__box" aria-hidden="true" />
      {hideLabel ? (
        <VisuallyHidden>{label}</VisuallyHidden>
      ) : (
        <span className="dpr-checkbox__label">{label}</span>
      )}
    </label>
  );
});
